/**
 * Pendo transport adapter over `@revalize/pendo`.
 *
 * The library owns the mechanics — CDN script loading (with dedupe, readiness
 * polling and retry), the provider implementations, and the React
 * `PendoProvider`/`PendoInitializer` lifecycle. This module owns exactly one
 * thing the library deliberately leaves to the consuming app: a **module-level
 * handle** to the active provider, so plain (non-React) modules can report
 * events.
 *
 * ## Why not `usePendo()`
 *
 * The library's canonical read path is the `usePendo()` hook. That does not fit
 * this codebase: `platform/analytics/pendoEvents` is imported by ~154 call
 * sites, and a large share of them are not components — `applyPricingEdit.ts`,
 * `treeToReaccessorizeInput.ts`, `CatalogCartStore` action creators,
 * `operations/api/reportHooks.ts`. A hook cannot be called from any of those.
 *
 * So the provider instance is created ONCE here, handed to the library's
 * `PendoProvider` for its lifecycle management, and also retained in a module
 * variable that `trackEvent()` reads. Both paths address the same object, so
 * React consumers could still use `usePendo()` if a component ever wants it.
 *
 * Every export is a safe no-op before the provider is installed (local dev with
 * no key, CI, an ad blocker). Analytics must never be able to break a flow —
 * the library's `PendoService` also try/catches every call.
 */

import {
  CdnPendoProvider,
  ConsolePendoProvider,
  NoopPendoProvider,
  type IPendoProvider,
} from '@revalize/pendo/frontend'

export type { PendoVisitor, PendoAccount } from '@revalize/pendo/frontend'

/**
 * The active provider, installed once by `createAppPendoProvider()`.
 *
 * `null` until the app root runs, which is why `trackEvent()` must tolerate it:
 * a "viewed"-style event can fire from a page that mounted before the provider
 * was constructed.
 */
let activeProvider: IPendoProvider | null = null

export interface CreatePendoProviderOptions {
  /** Agent key from `/api/config`. Absent ⇒ analytics disabled. */
  apiKey?: string | undefined
  /**
   * True in local dev / dev-auth mode. Selects `ConsolePendoProvider` so the
   * event stream is visible in the console without a Pendo subscription —
   * this replaces the previous hand-rolled `localStorage['pendo:debug']` flag.
   */
  isDevAuthMode: boolean
}

/**
 * Choose and install the provider implementation for this environment.
 *
 * Returns the instance so the caller can hand it to the library's
 * `PendoProvider`. Idempotent per distinct configuration: call it from a
 * `useMemo` keyed on `apiKey`/`isDevAuthMode` so React does not construct a new
 * provider on every render (the library remounts its initializer when the
 * provider identity changes).
 */
export function createAppPendoProvider(
  options: CreatePendoProviderOptions,
): IPendoProvider {
  const provider = selectProvider(options)
  activeProvider = provider
  return provider
}

function selectProvider({
  apiKey,
  isDevAuthMode,
}: CreatePendoProviderOptions): IPendoProvider {
  // Dev-auth mode first: even when a real key is present locally, logging to the
  // console is what you want rather than polluting the Pendo subscription with
  // localhost traffic.
  if (isDevAuthMode) return new ConsolePendoProvider()
  if (!apiKey) return new NoopPendoProvider()
  return new CdnPendoProvider()
}

/**
 * True when a provider is installed and its SDK reports ready.
 * Exposed for tests and diagnostics.
 */
export function isPendoActive(): boolean {
  return !!activeProvider && activeProvider.isReady()
}

/**
 * Send a custom track event.
 *
 * Prefer the named helpers in `@/platform/analytics/pendoEvents` over calling
 * this directly, so every tracked action stays discoverable in one place — a
 * boundary enforced by `frontend/src/architecture.test.ts`.
 */
export function trackEvent(eventName: string, metadata?: Record<string, unknown>): void {
  activeProvider?.track(eventName, metadata)
}

/**
 * Clear the installed provider. Test-only.
 * @internal
 */
export function resetPendoForTests(): void {
  activeProvider = null
}

/**
 * Install an arbitrary provider (e.g. `InMemoryPendoProvider`). Test-only —
 * lets a test assert on captured events without a React tree.
 * @internal
 */
export function setPendoProviderForTests(provider: IPendoProvider | null): void {
  activeProvider = provider
}
